import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AgentChatMessage } from '~/api/agentChat'
import RhapFeedbackButton, {
  FeedbackType,
} from '~/components/common/RhapFeedbackButton'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'

import { MarkdownRenderer } from './MarkdownComponents'
import { MessageAttachment } from './MessageAttachment'
import { MessageBubble } from './MessageBubble'

import styles from './ChatMessage.module.scss'

interface AgentMessageBubbleProps {
  isMostRecent?: boolean
  message: AgentChatMessage
  sessionId: string | null
}

export const AgentMessageBubble: React.FC<AgentMessageBubbleProps> = ({
  isMostRecent = false,
  message,
  sessionId,
}) => {
  const { t } = useTranslation('Agent')
  const [feedback, setFeedback] = useState<FeedbackType | null>(null)

  const handleFeedback = (type: FeedbackType) => {
    setFeedback(type)
    trackEvent(
      type === FeedbackType.POSITIVE
        ? TrackingEvent.AI_ASSISTANT_FEEDBACK_POSITIVE
        : TrackingEvent.AI_ASSISTANT_FEEDBACK_NEGATIVE,
      {
        messageId: message.id,
        messageContent: message.content,
        agent_session_id: sessionId,
        invocationId: message.invocationId,
      },
    )
  }

  return (
    <MessageBubble sender="agent">
      {message.isLoading ? (
        <div className={styles.loadingMessage}>
          <div className={styles.dots}>
            <div className={styles.dot} />
            <div className={styles.dot} />
            <div className={styles.dot} />
          </div>
        </div>
      ) : (
        <>
          <MarkdownRenderer content={message.content} sessionId={sessionId} />
          {message.attachments?.map((attachment, idx) => (
            <MessageAttachment
              attachment={attachment}
              // eslint-disable-next-line react/no-array-index-key
              key={`${message.id}-${attachment.mimeType}-${idx}`}
            />
          ))}
          <div className={styles.messageFooter}>
            {isMostRecent &&
              (message.isComplete === true || message.partial === false) && (
                <div className={styles.feedbackContainer}>
                  <span className={styles.feedbackQuestion}>
                    {t('FEEDBACK_QUESTION')}
                  </span>
                  <div className={styles.feedbackButtons}>
                    <RhapFeedbackButton
                      disabled={feedback !== null}
                      isSelected={feedback === FeedbackType.POSITIVE}
                      type={FeedbackType.POSITIVE}
                      onClick={() => handleFeedback(FeedbackType.POSITIVE)}
                    />
                    <RhapFeedbackButton
                      disabled={feedback !== null}
                      isSelected={feedback === FeedbackType.NEGATIVE}
                      type={FeedbackType.NEGATIVE}
                      onClick={() => handleFeedback(FeedbackType.NEGATIVE)}
                    />
                  </div>
                </div>
              )}
            <div className={styles.messageTimestamp}>{message.timestamp}</div>
          </div>
        </>
      )}
    </MessageBubble>
  )
}

export default AgentMessageBubble
